import { Text, Highlight } from "@chakra-ui/react";
import { UserRoleType } from "gql/graphql";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useMemo } from "react";
import colors from "app/colors";
import { CALLOUT_KEY } from "modules/utils/Constants";
import { EnvelopeIcon } from "modules/icons";
import useQueryString from "modules/hooks/use-query-string";

interface IProps {
  id: string;
  fontSize?: string;
  fontWeight?: number;
  overrideColor?: string;
  highlight?: boolean;
  fontFamily?: string;
  lineHeight?: string | number;
  letterSpacing?: string;
  fontStyle?: string;
}

const UserMention = ({
  id,
  fontSize,
  fontWeight = 500,
  overrideColor,
  highlight = true,
  fontFamily,
  lineHeight,
  letterSpacing,
  fontStyle,
}: IProps) => {
  const { usersById } = useUsers();
  const user = usersById[id];
  const { qsMessageHighlights } = useQueryString();

  const color = useMemo(() => {
    if (overrideColor) {
      return overrideColor;
    }
    if (user?.roleType === UserRoleType.Email) {
      return colors.blueGreen;
    }
    return colors.blue;
  }, [user, overrideColor]);

  const callout = useMemo(() => {
    if (!user) return "";
    if (user.roleType === UserRoleType.Email) {
      return user.email || "";
    }
    return user.callout || "";
  }, [user]);

  const calloutKey = useMemo(() => {
    if (!user) return "";
    if (user.roleType === UserRoleType.Email) {
      return (
        <EnvelopeIcon fill={colors.blueGreen} marginRight={1} boxSize={3} />
      );
    }
    return CALLOUT_KEY;
  }, [user]);

  const highlightQuery = highlight ? qsMessageHighlights : "";

  if (!user) {
    return null;
  }

  if (!user.active) {
    return (
      <Text
        color={overrideColor ?? colors.grayMain}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        lineHeight={lineHeight}
        letterSpacing={letterSpacing}
        fontStyle={fontStyle ?? "italic"}
        as="span"
      >
        {calloutKey}
        <Highlight query={highlightQuery} styles={{ bg: colors.yellow }}>
          {callout}
        </Highlight>
      </Text>
    );
  }

  return (
    <Text
      fontSize={fontSize}
      fontWeight={fontWeight}
      color={color}
      fontFamily={fontFamily}
      lineHeight={lineHeight}
      letterSpacing={letterSpacing}
      fontStyle={fontStyle}
      as="span"
    >
      {calloutKey}
      <Highlight query={highlightQuery} styles={{ bg: colors.yellow }}>
        {callout}
      </Highlight>
    </Text>
  );
};

export default memo(UserMention);
